// DOM bubbles in #fx (IMPLEMENTATION §2.7 "Snap-back", UX §3.9): two pooled nodes, transform-only,
// pointer-transparent, clamped to the viewport.
//   preview — above the lifted item while dragging / hovering a target: "→ Lead Lump · $12", "⇄", "+$12";
//             red "✕ Caster takes Lead" for an invalid target;
//   reason  — the red reason after a snap-back, for ui.json input.reasonBubbleMs (1.5 s).
// Text 14 px (ui.json input.bubblePx) with a 20 px icon (input.bubbleIconPx: "→ [icon] Lead Lump · $12",
// drawn into the bubble's one pooled canvas by the IconProvider); colours from palette.json
// (glow.invalid / ui.cream / ui.ink).
import type { IconKey, IconProvider } from './icons.ts';

export interface BubbleStyle {
  fontPx: number;
  /** The preview icon's side in CSS px (ui.json input.bubbleIconPx). */
  iconPx: number;
  /** Viewport edge margin and the gap above the anchor (ui.json input.bubbleGapPx). */
  gapPx: number;
  ink: string;
  paper: string;
  bad: string;
  icons: IconProvider;
}

/** An icon inside the preview text, before the character at index `at`. */
export interface BubbleIcon {
  key: IconKey;
  at: number;
}

export interface Bubbles {
  preview(text: string | null, bad: boolean, x: number, y: number, icon?: BubbleIcon | null): void;
  reason(text: string, x: number, y: number, ms: number): void;
  /** Diagnostics: the texts shown now. */
  shown(): { preview: string | null; reason: string | null };
  destroy(): void;
}

export function createBubbles(fxRoot: HTMLElement, st: BubbleStyle): Bubbles {
  const doc = fxRoot.ownerDocument;
  const win = doc.defaultView;
  const make = (cls: string): HTMLElement => {
    const el = doc.createElement('div');
    el.className = cls;
    el.setAttribute('aria-hidden', 'true');
    el.style.cssText =
      'position:absolute;left:0;top:0;display:none;white-space:nowrap;pointer-events:none;' +
      `font:700 ${st.fontPx}px/1.2 ui-rounded,system-ui,sans-serif;padding:${st.fontPx / 3}px ${st.fontPx / 1.5}px;` +
      `border-radius:${st.fontPx}px;box-shadow:0 3px 8px rgba(0,0,0,.35);will-change:transform;`;
    fxRoot.appendChild(el);
    return el;
  };
  const pv = make('gm-bubble gm-bubble-preview');
  const rs = make('gm-bubble gm-bubble-reason');
  // the preview: text before the icon, the icon canvas, text after it
  const pvA = doc.createElement('span');
  const pvIcon = doc.createElement('canvas');
  const pvB = doc.createElement('span');
  pvIcon.style.cssText =
    `display:none;width:${st.iconPx}px;height:${st.iconPx}px;vertical-align:middle;` +
    `margin:0 ${st.fontPx / 5}px 0 0;`;
  pv.appendChild(pvA);
  pv.appendChild(pvIcon);
  pv.appendChild(pvB);
  let pvText: string | null = null;
  let pvIconSig = '';
  let rsText: string | null = null;
  let rsTimer: ReturnType<typeof setTimeout> | null = null;

  /** Centres the bubble above (x, y), clamped inside the viewport. */
  const put = (el: HTMLElement, x: number, y: number): void => {
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    const vw = win?.innerWidth ?? w;
    const left = Math.min(Math.max(st.gapPx, x - w / 2), Math.max(st.gapPx, vw - w - st.gapPx));
    const top = Math.max(st.gapPx, y - h);
    el.style.transform = `translate3d(${left}px,${top}px,0)`;
  };
  const paint = (el: HTMLElement, bad: boolean): void => {
    el.style.background = bad ? st.bad : st.paper;
    el.style.color = bad ? st.paper : st.ink;
  };

  return {
    preview(text, bad, x, y, icon = null) {
      if (text === null) {
        if (pvText !== null) pv.style.display = 'none';
        pvText = null;
        return;
      }
      const sig = icon === null ? '' : `${JSON.stringify(icon.key)}@${icon.at}`;
      if (text !== pvText || sig !== pvIconSig) {
        const at = icon === null ? text.length : Math.max(0, Math.min(text.length, icon.at));
        pvA.textContent = text.slice(0, at);
        pvB.textContent = text.slice(at);
        const drawn = icon !== null && st.icons.draw(icon.key, pvIcon, st.iconPx);
        pvIcon.style.display = drawn ? 'inline-block' : 'none';
        paint(pv, bad);
        pvText = text;
        pvIconSig = sig;
      }
      pv.style.display = 'block';
      put(pv, x, y);
    },
    reason(text, x, y, ms) {
      if (rsTimer !== null) clearTimeout(rsTimer);
      rs.textContent = text;
      rsText = text;
      paint(rs, true);
      rs.style.display = 'block';
      put(rs, x, y - st.gapPx);
      rsTimer = setTimeout(() => {
        rs.style.display = 'none';
        rsText = null;
        rsTimer = null;
      }, ms);
    },
    shown: () => ({ preview: pvText, reason: rsText }),
    destroy() {
      if (rsTimer !== null) clearTimeout(rsTimer);
      pv.remove();
      rs.remove();
    },
  };
}
